import React from "react";
import Reveal from "./Reveal";
import { FaGithub } from "react-icons/fa";
import { VscRepo, VscStarEmpty, VscRepoForked, VscArrowRight } from "react-icons/vsc";
import arrayList from "../assets/portfolio/arrayList.png";
import javaFX from "../assets/portfolio/javaFX.PNG";
import leetCode from "../assets/portfolio/leetCode.png";
import fyp from "../assets/portfolio/fyp.PNG";
import db from "../assets/portfolio/discord.png";

const projects = [
  {
    id: 1,
    src: fyp,
    featured: true,
    repo: "FinalYearProject",
    title: "Mindful — MERN Social App",
    link: "https://github.com/khubaybk/FinalYearProject",
    lang: "JavaScript",
    langColor: "#f1e05a",
    tags: ["mongodb", "express", "react", "node"],
    text: "My final-year project: a full-stack social platform designed around mental well-being, automatically logging users out after a set time to encourage healthier online habits. Connect, post and engage across a seamless MERN experience.",
  },
  {
    id: 2,
    src: javaFX,
    repo: "JavaFX-educational-game",
    title: "JavaFX Educational Game",
    link: "https://github.com/khubaybk/JavaFX-educational-game",
    lang: "Java",
    langColor: "#b07219",
    tags: ["java", "javafx", "mvc", "singleton"],
    text: "A scientific space-invader game built on MVC architecture with the Singleton pattern — clean data separation and a single control center for an organised, challenging experience.",
  },
  {
    id: 3,
    src: db,
    repo: "discordCODBot",
    title: "Warzone Stats Discord Bot",
    link: "https://github.com/khubaybk/discordCODBot",
    lang: "Java",
    langColor: "#b07219",
    tags: ["java", "discord-api", "rest"],
    text: "A Java-powered Discord bot that fetches real-time Call of Duty: Warzone stats — kills, wins and K/D ratios — via API calls, keeping players informed and engaged.",
  },
  {
    id: 4,
    src: leetCode,
    repo: "LeetCodeDSA",
    title: "Algorithms & DSA",
    link: "https://github.com/khubaybk/LeetCodeDSA",
    lang: "Java",
    langColor: "#b07219",
    tags: ["algorithms", "data-structures"],
    text: "Consistent LeetCode practice honing problem-solving: dissecting complex challenges, spotting core patterns and crafting efficient, optimised solutions.",
  },
  {
    id: 5,
    src: arrayList,
    repo: "ToDoList",
    title: "To-Do List — ArrayList",
    link: "https://github.com/khubaybk/ToDoList",
    lang: "Java",
    langColor: "#b07219",
    tags: ["java", "data-structures"],
    text: "A task-management app demonstrating a strong grasp of data structures, implementing an ArrayList to store and manage tasks efficiently.",
  },
];

const RepoCard = ({ project, featured }) => (
  <div
    className={`group win flex h-full flex-col transition-all duration-300 hover:-translate-y-1 hover:border-syntax-fn/50 ${
      featured ? "md:flex-row" : ""
    }`}
  >
    {/* Preview */}
    <div className={`relative overflow-hidden border-b border-editor-border ${featured ? "md:w-1/2 md:border-b-0 md:border-r" : ""}`}>
      <img
        src={project.src}
        alt={project.title}
        className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
          featured ? "h-56 md:h-full" : "h-44"
        }`}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-editor-panel/60 to-transparent" />
      {featured && (
        <span className="absolute left-3 top-3 rounded-md border border-syntax-num/40 bg-editor-inset/90 px-2 py-0.5 text-xs text-syntax-num">
          ★ featured
        </span>
      )}
    </div>

    {/* Body */}
    <div className={`flex flex-1 flex-col p-5 ${featured ? "md:justify-center md:p-7" : ""}`}>
      <div className="flex items-center gap-2 text-sm">
        <VscRepo className="text-syntax-comment" />
        <a
          href={project.link}
          target="_blank"
          rel="noreferrer"
          className="truncate text-syntax-fn hover:underline"
        >
          khubaybk/{project.repo}
        </a>
      </div>

      <h3 className="mt-3 font-bold text-white">{project.title}</h3>
      <p className="mt-2 text-[0.85rem] leading-relaxed text-zinc-400">{project.text}</p>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {project.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-syntax-fn/20 bg-syntax-fn/5 px-2.5 py-0.5 text-xs text-syntax-fn"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Repo footer */}
      <div className="mt-5 flex items-center gap-4 border-t border-editor-border pt-4 text-xs text-syntax-comment">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: project.langColor }} />
          {project.lang}
        </span>
        <span className="flex items-center gap-1"><VscStarEmpty /> star</span>
        <span className="flex items-center gap-1"><VscRepoForked /> fork</span>
        <a
          href={project.link}
          target="_blank"
          rel="noreferrer"
          className="ml-auto flex items-center gap-1 text-syntax-str transition-colors hover:text-white"
        >
          <FaGithub /> code <VscArrowRight />
        </a>
      </div>
    </div>
  </div>
);

const Portfolio = () => {
  return (
    <section name="projects" className="relative w-full py-24 md:py-28">
      <div className="mx-auto max-w-5xl px-4">
        <Reveal className="mb-3">
          <p className="kicker">
            <span className="text-syntax-comment">04 /</span>
            <span className="text-syntax-prop">projects</span>
          </p>
        </Reveal>
        <Reveal className="mb-10">
          <p className="prompt text-syntax-comment">
            <span className="text-zinc-300">ls ~/projects</span>
            <span className="ml-2 text-syntax-comment">— {projects.length} repositories</span>
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {projects.map((project, i) => (
            <Reveal
              key={project.id}
              delay={(i % 2) * 80}
              className={`h-full ${project.featured ? "md:col-span-2" : ""}`}
            >
              <RepoCard project={project} featured={project.featured} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
